/**
 * One bar split into the parts of a whole, with an optional marker on top of it.
 *
 * Two places need the same shape and neither is served by {@link DistributionBar}, which draws one
 * value against a total: the NPS buckets (detractors, passives, promoters are three slices of the
 * same 100%) and a grid row (Ruim → Ótimo is an ordered scale, and what matters is where the mass
 * sits, not four separate bars). Stacked as one bar, three aspects of a grid take three lines
 * instead of twelve.
 *
 * The marker is the row's **average as a position on the scale** — the first option is 1 — drawn
 * where it falls rather than only printed as a number. "3,4" alone says nothing; a tick sitting
 * between Bom and Ótimo says it at a glance.
 *
 * @module Shared/components
 */

import { css } from '@emotion/css';
import type { JSX } from 'react';

const wrapStyle = css`
  position: relative;
  display: flex;
  width: 100%;
  height: 14px;
  border-radius: var(--radius-round);
  background: var(--color-surface-sunken);
  overflow: hidden;
`;

const segmentStyle = css`
  height: 100%;
  min-width: 0;
`;

/**
 * The marker rides above the bar, not inside a segment.
 *
 * `overflow: hidden` on the bar would clip it, so it lives in an absolutely positioned sibling —
 * which also keeps it exact when the segments round.
 */
const markerWrapStyle = css`
  position: absolute;
  inset: 0;
  pointer-events: none;
`;

const markerStyle = css`
  position: absolute;
  top: -3px;
  bottom: -3px;
  width: 3px;
  margin-left: -1.5px;
  border-radius: 2px;
  background: var(--color-text);
  /* A white collar, so the tick stays visible on the dark end of the scale as well as the pale one. */
  box-shadow: 0 0 0 1.5px var(--color-surface);
`;

const emptyStyle = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 14px;
  border: 1px dashed var(--color-border-strong);
  border-radius: var(--radius-round);
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
  white-space: nowrap;
`;

export interface BarSegment {
  /** Read out in the title, so a slice too thin to click can still be identified. */
  label: string;
  value: number;
  color: string;
}

export interface SegmentedBarProps {
  segments: BarSegment[];
  /** Where to put the tick, from 0 to 1. Omitted when the row has no average — a BOOLEAN row. */
  markerAt?: number | null;
  /** What the marker means, for the title. */
  markerLabel?: string;
  /** Accessible summary: the shape carries the meaning, so it needs saying in words. */
  label: string;
  emptyText?: string;
}

export const SegmentedBar = ({
  segments,
  markerAt,
  markerLabel,
  label,
  emptyText = 'ninguém respondeu',
}: SegmentedBarProps): JSX.Element => {
  const total = segments.reduce((sum, segment) => sum + segment.value, 0);

  if (total === 0) return <span className={emptyStyle}>{emptyText}</span>;

  return (
    <span className={wrapStyle} role='img' aria-label={label}>
      {segments.map(segment => {
        if (segment.value === 0) return null;

        const share = (segment.value / total) * 100;

        return (
          <span
            key={segment.label}
            className={segmentStyle}
            title={`${segment.label}: ${segment.value.toLocaleString('pt-BR')} (${Math.round(share)}%)`}
            style={{ width: `${share}%`, background: segment.color }}
          />
        );
      })}

      {markerAt === null || markerAt === undefined ? null : (
        <span className={markerWrapStyle}>
          <span
            className={markerStyle}
            title={markerLabel}
            style={{ left: `${Math.min(Math.max(markerAt, 0), 1) * 100}%` }}
          />
        </span>
      )}
    </span>
  );
};
